(() => {
  const FOCUSABLE_SELECTOR = ['a[href]', 'button:not([disabled])', '[tabindex]:not([tabindex="-1"])'].join(',');

  // 向 dataLayer 推送头部交互事件（hover 展开菜单、切换产品分组等，GTM 点击触发器覆盖不到的动作）。
  // 字段结构与主题现有 market/* 事件保持一致；埋点失败不影响交互。
  function pushHeaderEvent(event, detail) {
    try {
      window.zzAnalytics && window.zzAnalytics.trackEvent(event, {
        timestamp: new Date().toISOString(),
        isApp: window.USER_AGENT?.isApp,
        uaMessage: window.USER_AGENT,
        ...detail,
      });
      // window.dataLayer = window.dataLayer || [];
      // window.dataLayer.push({
      //   event,
      //   timestamp: new Date().toISOString(),
      //   isApp: window.USER_AGENT?.isApp,
      //   uaMessage: window.USER_AGENT,
      //   ...detail,
      // });
    } catch (error) {
      console.error(error);
    }
  }

  class HeaderV4TemplateHydrator {
    constructor(root) {
      this.root = root;
      this.templates = new Map(
        [...root.querySelectorAll('[data-header-v4-template]')].map((template) => [
          template.dataset.headerV4Template,
          template,
        ]),
      );
    }

    hydrate(target) {
      if (!target || target.dataset.headerV4Hydrated === 'true') return;
      const key = target.dataset.headerV4Target;
      const template = this.templates.get(key);
      if (template) target.append(template.content.cloneNode(true));
      target.dataset.headerV4Hydrated = 'true';
    }

    hydrateWithin(container) {
      if (!container) return;
      if (container.matches('[data-header-v4-target]')) this.hydrate(container);
      container.querySelectorAll('[data-header-v4-target]').forEach((target) => this.hydrate(target));
    }

    hydrateDirect(container) {
      container?.querySelectorAll('[data-header-v4-direct-target]').forEach((target) => this.hydrate(target));
    }
  }

  class HeaderV4DesktopController {
    constructor(host, hydrator) {
      this.host = host;
      this.root = host.closest('header-v4');
      this.hydrator = hydrator;
      this.activeMenu = '';
      this.closeTimer = null;
      this.openTimer = null;
      this.pendingMenu = '';
      this.lastPointerType = '';
      this.restoringTriggerFocus = false;
      this.listeners = [];
      this.triggers = [...host.querySelectorAll('[data-header-v4-desktop-trigger]')];
      this.panels = [...host.querySelectorAll('[data-header-v4-desktop-panel]')];
      this.overlay = this.root?.querySelector('.header-v4__overlay');
      this.handleClick = this.handleClick.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleHostKeydown = this.handleHostKeydown.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerOver = this.handlePointerOver.bind(this);
      this.handlePointerOut = this.handlePointerOut.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    get isOpen() {
      return Boolean(this.activeMenu);
    }

    listen(target, type, handler, options) {
      target?.addEventListener(type, handler, options);
      if (target) this.listeners.push([target, type, handler, options]);
    }

    init() {
      // 记录最近一次按压的指针类型，供 click 回调区分鼠标/触屏/键盘
      this.listen(
        this.host,
        'pointerdown',
        (event) => {
          this.lastPointerType = event.pointerType || '';
        },
        { capture: true },
      );
      this.listen(this.host, 'pointerover', this.handlePointerOver);
      this.listen(this.host, 'pointerout', this.handlePointerOut);
      this.listen(this.host, 'focusin', this.handleFocusIn);
      this.listen(this.host, 'click', this.handleClick);
      this.listen(this.host, 'keydown', this.handleHostKeydown);
      this.listen(this.host, 'pointerenter', () => this.cancelClose());
      this.listen(this.host, 'pointerleave', (event) => {
        // 触屏抬起后也会触发 pointerleave，不能据此关闭面板（关闭交给点外部/overlay）
        if (event.pointerType === 'touch' || event.pointerType === 'pen') return;
        this.cancelOpen();
        this.scheduleClose();
      });
      this.listen(this.overlay, 'click', () => this.close());
      this.listen(document, 'keydown', this.handleDocumentKeydown);
      this.listen(document, 'pointerdown', this.handlePointerDown);
      this.listen(window, 'resize', this.handleResize, { passive: true });
      this.panels.forEach((panel) => {
        panel.hidden = panel.getAttribute('aria-hidden') !== 'false';
        this.normalizeTabs(panel);
      });
    }

    normalizeTabs(panel) {
      const tabs = this.getTabs(panel);
      const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
      tabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
    }

    getTabs(panel) {
      return [...panel.querySelectorAll('[data-header-v4-desktop-product]')];
    }

    getPanelForElement(element) {
      return element?.closest('[data-header-v4-desktop-panel]');
    }

    getTriggerLabel(name) {
      const trigger = this.triggers.find((item) => item.dataset.headerV4DesktopTrigger === name);
      return trigger?.textContent.trim() || name;
    }

    open(name, { via = 'hover' } = {}) {
      if (!name) return;
      this.cancelClose();
      this.cancelOpen();
      // 重复 hover 同一菜单（面板未关闭）时保留当前选中分组；全新打开则重置为默认分组
      const reopening = this.activeMenu === name;
      this.activeMenu = name;
      this.root.dataset.open = 'true';
      this.root.__headerV4Controllers?.scroll?.show();
      this.triggers.forEach((trigger) =>
        trigger.setAttribute('aria-expanded', String(trigger.dataset.headerV4DesktopTrigger === name)),
      );
      this.panels.forEach((panel) => {
        const active = panel.dataset.headerV4DesktopPanel === name;
        panel.setAttribute('aria-hidden', String(!active));
        panel.hidden = !active;
        if (active) {
          this.hydrator.hydrateDirect(panel);
          this.ensureProduct(panel, { reset: !reopening });
        }
      });
      if (!reopening) pushHeaderEvent('market/header_menu_open', { menu: this.getTriggerLabel(name), via });
    }

    close({ restoreFocus = false } = {}) {
      this.cancelOpen();
      const previous = this.triggers.find((trigger) => trigger.dataset.headerV4DesktopTrigger === this.activeMenu);
      this.activeMenu = '';
      this.root.dataset.open = 'false';
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((panel) => {
        panel.setAttribute('aria-hidden', 'true');
        panel.hidden = true;
      });
      if (restoreFocus && previous) {
        this.restoringTriggerFocus = true;
        previous.focus();
        this.restoringTriggerFocus = false;
      }
    }

    activateProduct(panel, button, { focus = false, via = 'hover' } = {}) {
      if (!panel || !button) return;
      // 已选中的分组重复触发（hover 抖动、click 前的 focusin）不重复埋点
      const changed = button.getAttribute('aria-selected') !== 'true';
      const key = button.dataset.headerV4DesktopProduct;
      this.getTabs(panel).forEach((item) => {
        const active = item === button;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panel.querySelectorAll('[data-header-v4-desktop-group]').forEach((group) => {
        const active = group.dataset.headerV4DesktopGroup === key;
        if (active) this.hydrator.hydrateWithin(group);
        group.hidden = !active;
        group.classList.toggle('is-active', active);
        group.setAttribute('aria-hidden', String(!active));
      });
      if (focus) button.focus();
      // via 为 auto 时是打开面板的默认激活，不算用户切换
      if (changed && via !== 'auto')
        pushHeaderEvent('market/header_group_switch', {
          menu: this.getTriggerLabel(this.activeMenu),
          group: button.textContent.trim(),
          via,
        });
    }

    // reset 为 true 时忽略上次的选中态，回到默认分组（default_group，未配置则为第一个）
    ensureProduct(panel, { reset = false } = {}) {
      let current = null;
      if (!reset) current = panel.querySelector('[data-header-v4-desktop-product][aria-selected="true"]');
      this.activateProduct(
        panel,
        current ||
          panel.querySelector('[data-header-v4-desktop-product][data-header-v4-desktop-default]') ||
          panel.querySelector('[data-header-v4-desktop-product]'),
        { via: 'auto' },
      );
    }

    moveTab(panel, current, key) {
      const tabs = this.getTabs(panel);
      const index = tabs.indexOf(current);
      if (index < 0 || !tabs.length) return;
      let nextIndex = index;
      if (key === 'Home') nextIndex = 0;
      else if (key === 'End') nextIndex = tabs.length - 1;
      else if (key === 'ArrowUp' || key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      else if (key === 'ArrowDown' || key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      else return;
      this.activateProduct(panel, tabs[nextIndex], { focus: true, via: 'keyboard' });
    }

    scheduleClose() {
      this.cancelClose();
      this.cancelOpen();
      this.closeTimer = window.setTimeout(() => this.close(), 140);
    }

    cancelClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    // 尚未有面板打开时，悬停延迟展开：避免鼠标横扫导航栏时面板逐个闪现。
    // 已有面板打开时切换相邻项则保持即时响应。
    scheduleOpen(name) {
      if (!name) return;
      if (this.isOpen) {
        this.open(name);
        return;
      }
      if (this.pendingMenu === name && this.openTimer) return;
      this.cancelOpen();
      this.pendingMenu = name;
      this.openTimer = window.setTimeout(() => {
        this.openTimer = null;
        this.pendingMenu = '';
        this.open(name);
      }, 120);
    }

    cancelOpen() {
      window.clearTimeout(this.openTimer);
      this.openTimer = null;
      this.pendingMenu = '';
    }

    handlePointerOver(event) {
      // 触屏的 pointerover 先于 click 触发，交给 click 统一处理开/关，
      // 避免大屏触摸设备（iPad 横屏等）上 tap 展开后又被 click 收起
      if (event.pointerType === 'touch' || event.pointerType === 'pen') return;
      const trigger = event.target.closest('[data-header-v4-desktop-trigger]');
      if (trigger && !trigger.contains(event.relatedTarget)) {
        this.scheduleOpen(trigger.dataset.headerV4DesktopTrigger);
        return;
      }
      const product = event.target.closest('[data-header-v4-desktop-product]');
      if (product && !product.contains(event.relatedTarget))
        this.activateProduct(this.getPanelForElement(product), product);
    }

    handlePointerOut(event) {
      const trigger = event.target.closest('[data-header-v4-desktop-trigger]');
      if (trigger && !trigger.contains(event.relatedTarget) && this.pendingMenu === trigger.dataset.headerV4DesktopTrigger)
        this.cancelOpen();
    }

    // 鼠标按压引起的聚焦不匹配 :focus-visible，用它区分键盘焦点与点击前置的 focusin
    isKeyboardFocus(element) {
      try {
        return element.matches(':focus-visible');
      } catch (error) {
        return true;
      }
    }

    handleFocusIn(event) {
      const trigger = event.target.closest('[data-header-v4-desktop-trigger]');
      if (trigger) {
        if (this.restoringTriggerFocus) return;
        if (!this.isKeyboardFocus(trigger)) return;
        this.open(trigger.dataset.headerV4DesktopTrigger, { via: 'focus' });
        return;
      }
      const product = event.target.closest('[data-header-v4-desktop-product]');
      if (product && this.isKeyboardFocus(product))
        this.activateProduct(this.getPanelForElement(product), product, { via: 'focus' });
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-header-v4-desktop-trigger]');
      if (trigger) {
        const name = trigger.dataset.headerV4DesktopTrigger;
        if (this.activeMenu === name) {
          // 键盘激活（detail 为 0）和触屏 tap 允许收起面板；
          // 鼠标点击时面板多半已被 hover 打开，此时点击保持展开，避免误关
          const byKeyboard = event.detail === 0;
          const byTouch = this.lastPointerType === 'touch' || this.lastPointerType === 'pen';
          if (byKeyboard || byTouch) this.close({ restoreFocus: byKeyboard });
          return;
        }
        this.open(name, { via: 'click' });
        return;
      }
      const product = event.target.closest('[data-header-v4-desktop-product]');
      if (product) this.activateProduct(this.getPanelForElement(product), product, { via: 'click' });
    }

    handleHostKeydown(event) {
      const product = event.target.closest('[data-header-v4-desktop-product]');
      if (!product) return;
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.moveTab(this.getPanelForElement(product), product, event.key);
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close({ restoreFocus: true });
      }
    }

    handlePointerDown(event) {
      if (this.activeMenu && !this.host.contains(event.target) && !this.overlay?.contains(event.target)) this.close();
    }

    handleResize() {
      if (window.innerWidth < 1024 && this.activeMenu) this.close();
    }

    destroy() {
      this.listeners.forEach(([target, type, handler, options]) => target.removeEventListener(type, handler, options));
      this.listeners = [];
      this.cancelClose();
      this.cancelOpen();
    }
  }

  class HeaderV4MobileController {
    constructor(host, hydrator) {
      this.host = host;
      this.root = host.closest('header-v4');
      this.hydrator = hydrator;
      this.drawer = host.querySelector('[data-header-v4-mobile-drawer]');
      this.primary = host.querySelector('[data-header-v4-mobile-primary]');
      this.normalizeMarkup();
      this.menuButton = host.querySelector('[data-header-v4-mobile-menu]');
      this.panels = [...host.querySelectorAll('[data-header-v4-mobile-panel]')];
      this.activeMenu = '';
      this.listeners = [];
      this.lastFocusedElement = null;
      this.inertBranches = new Map();
      this.scrollResetFrame = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    normalizeMarkup() {
      if (!this.primary || !this.drawer) return;
      this.host.querySelectorAll('[data-header-v4-mobile-panel]').forEach((panel) => {
        const trigger = this.primary.querySelector(`[aria-controls="${panel.id}"]`);
        if (!trigger) return;
        let item = trigger.closest('[data-header-v4-mobile-item]');
        if (!item) {
          item = document.createElement('div');
          item.className = 'header-v4__mobile-nav-item';
          item.dataset.headerV4MobileItem = trigger.dataset.headerV4MobileTrigger || panel.id;
          trigger.parentNode.insertBefore(item, trigger);
          item.append(trigger);
        }
        if (panel.parentElement !== item) item.append(panel);
      });
    }

    get triggers() {
      return [...this.host.querySelectorAll('[data-header-v4-mobile-trigger]')];
    }

    get isOpen() {
      return this.root.classList.contains('is-mobile-drawer-open');
    }

    listen(target, type, handler, options) {
      target?.addEventListener(type, handler, options);
      if (target) this.listeners.push([target, type, handler, options]);
    }

    init() {
      this.listen(this.host, 'click', this.handleClick);
      this.listen(document, 'keydown', this.handleKeydown);
      this.listen(document, 'pointerdown', this.handlePointerDown);
      this.listen(window, 'resize', this.handleResize, { passive: true });
      this.panels.forEach((panel) => {
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'true');
      });
      this.host.querySelectorAll('.header-v4__mobile-product-content').forEach((content) => {
        content.hidden = false;
        content.setAttribute('aria-hidden', 'true');
      });
      this.syncSurfaceState();
    }

    openDrawer() {
      if (!this.isOpen) this.lastFocusedElement = document.activeElement;
      this.root.classList.add('is-mobile-drawer-open');
      this.root.dataset.open = 'true';
      this.root.__headerV4Controllers?.scroll?.show();
      document.documentElement.classList.add('header-v4-mobile-lock');
      this.host.setAttribute('role', 'dialog');
      this.host.setAttribute('aria-modal', 'true');
      this.host.setAttribute('aria-label', this.menuButton?.dataset.labelOpen || 'Menu');
      this.setOutsideInert(true);
      this.menuButton?.setAttribute('aria-expanded', 'true');
      this.menuButton?.setAttribute('aria-label', this.menuButton.dataset.labelClose || 'Close');
      this.drawer?.setAttribute('aria-hidden', 'false');
      this.syncSurfaceState();
      this.resetDrawerScroll({ defer: true });
      if (!this.host.contains(document.activeElement)) this.menuButton?.focus();
    }

    toggleMenuItem(trigger) {
      if (!trigger) return;
      if (!this.isOpen) this.openDrawer();
      const item = trigger.closest('[data-header-v4-mobile-item]');
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!item || !panel || !this.host.contains(panel)) return;
      const expanded = trigger.getAttribute('aria-expanded') !== 'true';
      this.activeMenu = expanded ? panel.id : '';
      this.root.classList.toggle('is-mobile-panel-open', expanded);
      this.triggers.forEach((otherTrigger) => {
        const otherItem = otherTrigger.closest('[data-header-v4-mobile-item]');
        const otherPanelId = otherTrigger.getAttribute('aria-controls');
        const otherPanel = otherPanelId ? document.getElementById(otherPanelId) : null;
        const active = otherTrigger === trigger && expanded;
        otherTrigger.setAttribute('aria-expanded', String(active));
        otherItem?.classList.toggle('is-expanded', active);
        if (otherPanel && this.host.contains(otherPanel)) {
          otherPanel.hidden = false;
          otherPanel.setAttribute('aria-hidden', String(!active));
          if (active) {
            this.hydrator.hydrateDirect(otherPanel);
            this.resetProducts(otherPanel);
          }
        }
      });
      this.syncSurfaceState();
    }

    toggleProduct(trigger) {
      const item = trigger.closest('[data-header-v4-mobile-product]');
      const content = item?.querySelector('.header-v4__mobile-product-content');
      if (!item || !content) return;
      const expanded = !item.classList.contains('is-expanded');
      item
        .closest('[data-header-v4-mobile-products]')
        ?.querySelectorAll('[data-header-v4-mobile-product]')
        .forEach((other) => {
          if (other === item) return;
          other.classList.remove('is-expanded', 'is-active');
          other.querySelector('[data-header-v4-mobile-product-trigger]')?.setAttribute('aria-expanded', 'false');
          const otherContent = other.querySelector('.header-v4__mobile-product-content');
          if (otherContent) {
            otherContent.hidden = false;
            otherContent.setAttribute('aria-hidden', 'true');
            otherContent.scrollLeft = 0;
          }
        });
      item.classList.toggle('is-expanded', expanded);
      item.classList.toggle('is-active', expanded);
      trigger.setAttribute('aria-expanded', String(expanded));
      if (expanded) this.hydrator.hydrateWithin(content);
      content.hidden = false;
      content.setAttribute('aria-hidden', String(!expanded));
      this.syncSurfaceState();
    }

    resetProducts(panel) {
      const items = [...panel.querySelectorAll('[data-header-v4-mobile-product]')];
      if (!items.length) {
        this.hydrator.hydrateDirect(panel);
        return;
      }
      items.forEach((item) => {
        item.classList.remove('is-expanded', 'is-active');
        item.querySelector('[data-header-v4-mobile-product-trigger]')?.setAttribute('aria-expanded', 'false');
        const content = item.querySelector('.header-v4__mobile-product-content');
        if (content) {
          content.hidden = false;
          content.setAttribute('aria-hidden', 'true');
          content.scrollLeft = 0;
        }
      });
      panel.querySelector('[data-header-v4-mobile-products]')?.scrollTo(0, 0);
      // 打开面板时默认展开默认分组（is-default，即 default_group 或第一个有内容的分组）
      const defaultItem = panel.querySelector('[data-header-v4-mobile-product].is-default') || items[0];
      const defaultTrigger = defaultItem?.querySelector('[data-header-v4-mobile-product-trigger]');
      if (defaultTrigger) this.toggleProduct(defaultTrigger);
    }

    getFocusableElements() {
      return [...this.host.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => !element.closest('[inert]') && !element.closest('[hidden]') && element.getClientRects().length > 0,
      );
    }

    setOutsideInert(active) {
      if (!active) {
        this.inertBranches.forEach((wasInert, element) => {
          element.inert = wasInert;
        });
        this.inertBranches.clear();
        return;
      }
      // 将抽屉之外的页面分支设为 inert，使 aria-modal 名副其实；
      // 但跳过承载全局弹窗（国家选择器、购物车抽屉等）的分支——
      // 它们层级在头部抽屉之上，必须保持可交互。
      const GLOBAL_DIALOG_SELECTOR = '.maskframeModal, .maskframeCover, cart-drawer, #IPTipsBox';
      let node = this.root;
      while (node && node !== document.body && node.parentElement) {
        [...node.parentElement.children].forEach((sibling) => {
          if (sibling === node) return;
          if (!(sibling instanceof HTMLElement)) return;
          if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sibling.tagName)) return;
          if (sibling.matches(GLOBAL_DIALOG_SELECTOR) || sibling.querySelector(GLOBAL_DIALOG_SELECTOR)) return;
          if (!this.inertBranches.has(sibling)) this.inertBranches.set(sibling, sibling.inert);
          sibling.inert = true;
        });
        node = node.parentElement;
      }
    }

    resetDrawerScroll({ defer = false } = {}) {
      if (!this.drawer) return;
      const reset = () => {
        this.drawer.scrollTop = 0;
        this.drawer.scrollLeft = 0;
        if (this.primary) {
          this.primary.scrollTop = 0;
          this.primary.scrollLeft = 0;
        }
      };
      reset();
      if (!defer) return;
      if (this.scrollResetFrame) cancelAnimationFrame(this.scrollResetFrame);
      this.scrollResetFrame = requestAnimationFrame(() => {
        this.scrollResetFrame = null;
        if (this.isOpen) reset();
      });
    }

    close({ restoreFocus = false } = {}) {
      if (this.scrollResetFrame) cancelAnimationFrame(this.scrollResetFrame);
      this.scrollResetFrame = null;
      this.resetDrawerScroll();
      this.activeMenu = '';
      this.root.classList.remove('is-mobile-drawer-open');
      this.root.classList.remove('is-mobile-panel-open');
      this.root.dataset.open = 'false';
      document.documentElement.classList.remove('header-v4-mobile-lock');
      this.host.removeAttribute('role');
      this.host.removeAttribute('aria-modal');
      this.host.removeAttribute('aria-label');
      this.setOutsideInert(false);
      this.menuButton?.setAttribute('aria-expanded', 'false');
      this.menuButton?.setAttribute('aria-label', this.menuButton.dataset.labelOpen || 'Menu');
      this.drawer?.setAttribute('aria-hidden', 'true');
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.closest('[data-header-v4-mobile-item]')?.classList.remove('is-expanded');
      });
      this.panels.forEach((panel) => {
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'true');
        panel.scrollTo(0, 0);
      });
      this.host.querySelectorAll('[data-header-v4-mobile-product]').forEach((item) => {
        item.classList.remove('is-expanded', 'is-active');
        item.querySelector('[data-header-v4-mobile-product-trigger]')?.setAttribute('aria-expanded', 'false');
        const content = item.querySelector('.header-v4__mobile-product-content');
        if (content) {
          content.hidden = false;
          content.setAttribute('aria-hidden', 'true');
          content.scrollLeft = 0;
        }
      });
      this.primary?.scrollTo(0, 0);
      this.syncSurfaceState();
      if (restoreFocus) (this.lastFocusedElement || this.menuButton)?.focus();
      this.lastFocusedElement = null;
    }

    handleClick(event) {
      const menuButton = event.target.closest('[data-header-v4-mobile-menu]');
      if (menuButton) {
        this.isOpen ? this.close({ restoreFocus: true }) : this.openDrawer();
        return;
      }
      const cartTrigger = event.target.closest('[data-cart-drawer-trigger]');
      if (cartTrigger && this.isOpen) {
        this.close();
        return;
      }
      const trigger = event.target.closest('[data-header-v4-mobile-trigger]');
      if (trigger) {
        this.toggleMenuItem(trigger);
        return;
      }
      const productTrigger = event.target.closest('[data-header-v4-mobile-product-trigger]');
      if (productTrigger) this.toggleProduct(productTrigger);
    }

    handleKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ restoreFocus: true });
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = this.getFocusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handlePointerDown(event) {
      if (!this.isOpen || this.host.contains(event.target)) return;
      if (event.target.closest('.maskframeModal, .maskframeCover')) return;
      this.close({ restoreFocus: true });
    }

    handleResize() {
      if (window.innerWidth >= 1024 && this.isOpen) this.close();
    }

    syncSurfaceState() {
      if (this.primary) this.primary.inert = !this.isOpen;
      this.panels.forEach((panel) => {
        panel.inert = !this.isOpen || panel.getAttribute('aria-hidden') !== 'false';
      });
      this.host.querySelectorAll('.header-v4__mobile-product-content').forEach((content) => {
        content.inert = !this.isOpen || content.getAttribute('aria-hidden') !== 'false';
      });
      if (this.drawer) {
        this.drawer.hidden = false;
        this.drawer.inert = !this.isOpen;
      }
    }

    destroy() {
      this.listeners.forEach(([target, type, handler, options]) => target.removeEventListener(type, handler, options));
      this.listeners = [];
      if (this.scrollResetFrame) cancelAnimationFrame(this.scrollResetFrame);
      this.scrollResetFrame = null;
      this.close();
    }
  }

  class HeaderV4CartController {
    constructor(root) {
      this.root = root;
      this.primary = root.querySelector('#cart-icon-bubble');
      this.triggers = [...root.querySelectorAll('[data-cart-drawer-trigger]')];
      this.observer = null;
    }

    init() {
      document.querySelector('cart-drawer')?.setHeaderCartIconAccessibility?.();
      if (!this.primary || this.triggers.length < 2) return;
      this.sync();
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.primary, { childList: true, subtree: true });
    }

    sync() {
      const html = this.primary?.innerHTML;
      if (html == null) return;
      this.triggers.forEach((trigger) => {
        if (trigger !== this.primary && trigger.innerHTML !== html) trigger.innerHTML = html;
      });
    }

    destroy() {
      this.observer?.disconnect();
      this.observer = null;
    }
  }

  class HeaderV4ScrollController {
    constructor(root) {
      this.root = root;
      this.container = root.querySelector('.header-v4__container');
      this.lastScrollY = window.scrollY;
      this.frame = null;
      this.onScroll = this.onScroll.bind(this);
    }

    init() {
      if (!this.root.hasAttribute('data-hide-on-scroll')) return;
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    show() {
      this.container?.style.removeProperty('transform');
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        const current = window.scrollY;
        const mobileOpen = this.root.classList.contains('is-mobile-drawer-open');
        if (!mobileOpen && !this.root.__headerV4Controllers?.desktop?.activeMenu && this.container) {
          if (current > this.lastScrollY && current > 100)
            this.container.style.transform = 'translateY(-100%)';
          else this.container.style.removeProperty('transform');
        }
        this.lastScrollY = current;
        this.frame = null;
      });
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.show();
    }
  }

  function initialize(root) {
    if (root.__headerV4Controllers) return;
    const desktop = root.querySelector('[data-header-v4-desktop]');
    const mobile = root.querySelector('[data-header-v4-mobile]');
    const hydrator = new HeaderV4TemplateHydrator(root);
    root.__headerV4Controllers = {
      hydrator,
      desktop: desktop ? new HeaderV4DesktopController(desktop, hydrator) : null,
      mobile: mobile ? new HeaderV4MobileController(mobile, hydrator) : null,
      cart: new HeaderV4CartController(root),
      scroll: new HeaderV4ScrollController(root),
    };
    root.__headerV4Controllers.desktop?.init();
    root.__headerV4Controllers.mobile?.init();
    root.__headerV4Controllers.cart.init();
    root.__headerV4Controllers.scroll.init();
  }

  function destroy(root) {
    root.__headerV4Controllers?.desktop?.destroy();
    root.__headerV4Controllers?.mobile?.destroy();
    root.__headerV4Controllers?.cart?.destroy();
    root.__headerV4Controllers?.scroll?.destroy();
    delete root.__headerV4Controllers;
  }

  if (typeof customElements !== 'undefined' && !customElements.get('header-v4'))
    customElements.define(
      'header-v4',
      class extends HTMLElement {
        connectedCallback() {
          initialize(this);
        }

        disconnectedCallback() {
          destroy(this);
        }
      },
    );
  else {
    const boot = () => document.querySelectorAll('header-v4').forEach(initialize);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
    else boot();
  }
})();
